/**
 * FireBoundaryLayer Component
 *
 * Renders fire burn area polygons on the map showing the actual burnt extent of each fire.
 * Uses react-leaflet's Polygon/GeoJSON components to display the boundaries.
 *
 * NOTE: This layer shows BURN AREAS (actual fire perimeters) from:
 * - VIC OSOM 'burn-area' features (BURN_AREA events with burnGeometry)
 * - NSW RFS fire boundaries (FIRE_INCIDENT events with boundaryGeometry)
 * For warning zone polygons (affected areas), see WarningAreaLayer.tsx.
 *
 * PERFORMANCE: Only renders boundaries that intersect with the current map viewport.
 */

import { useState, useEffect, useMemo, memo, useRef } from 'react';
import { Polygon, Pane, useMap } from 'react-leaflet';
import { UnifiedEvent } from '../types/events';
import { LatLngExpression } from 'leaflet';

// Custom pane name for fire boundaries - rendered below overlay pane (markers)
// Leaflet default panes z-index: tile=200, overlay=400, shadow=500, marker=600, tooltip=650, popup=700
// We use 350 to render boundaries below the overlay pane where CircleMarkers are rendered
const FIRE_BOUNDARY_PANE = 'fireBoundaryPane';
const FIRE_BOUNDARY_PANE_Z = 350;

interface FireBoundaryLayerProps {
  events: UnifiedEvent[];
}

/**
 * RFS-style colors for fire boundaries.
 * Uses a grayish-green fill to represent burnt/affected areas,
 * matching the official NSW RFS Fires Near Me map styling.
 */
const RFS_BOUNDARY_FILL = '#a8b5a0'; // Grayish-green fill (burnt area)
const RFS_BOUNDARY_STROKE = '#2d3a2d'; // Dark green/charcoal outline

/**
 * Convert GeoJSON polygon coordinates to Leaflet LatLng format.
 * GeoJSON uses [lon, lat] but Leaflet uses [lat, lon].
 */
function convertCoordinates(coords: number[][]): LatLngExpression[] {
  return coords.map(([lon, lat]) => [lat, lon] as LatLngExpression);
}

/**
 * Extract polygon positions from boundary geometry.
 * Handles both Polygon and MultiPolygon types.
 */
function getPolygonPositions(
  boundaryGeometry: { type: string; coordinates: number[][][] | number[][][][] } | null
): LatLngExpression[][] | null {
  if (!boundaryGeometry) return null;

  if (boundaryGeometry.type === 'Polygon') {
    // Polygon coordinates are [[[lon, lat], ...]] - array of rings
    const coords = boundaryGeometry.coordinates as number[][][];
    // Return all rings (first is outer, rest are holes)
    return coords.map(ring => convertCoordinates(ring));
  } else if (boundaryGeometry.type === 'MultiPolygon') {
    // MultiPolygon coordinates are [[[[lon, lat], ...]]] - array of polygons
    const coords = boundaryGeometry.coordinates as number[][][][];
    // Flatten to just the outer rings of each polygon for simplicity
    return coords.map(polygon => convertCoordinates(polygon[0]));
  }

  return null;
}

/**
 * Calculate bounding box for a polygon geometry.
 * Returns [minLat, minLon, maxLat, maxLon] or null if invalid.
 */
function getBoundingBox(
  boundaryGeometry: { type: string; coordinates: number[][][] | number[][][][] } | null
): [number, number, number, number] | null {
  if (!boundaryGeometry) return null;

  let minLat = Infinity, minLon = Infinity;
  let maxLat = -Infinity, maxLon = -Infinity;

  const processCoords = (coords: number[][]) => {
    for (const [lon, lat] of coords) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
    }
  };

  if (boundaryGeometry.type === 'Polygon') {
    const coords = boundaryGeometry.coordinates as number[][][];
    coords.forEach(ring => processCoords(ring));
  } else if (boundaryGeometry.type === 'MultiPolygon') {
    const coords = boundaryGeometry.coordinates as number[][][][];
    coords.forEach(polygon => polygon.forEach(ring => processCoords(ring)));
  }

  if (minLat === Infinity) return null;
  return [minLat, minLon, maxLat, maxLon];
}


/**
 * Memoized single polygon renderer
 */
const FirePolygon = memo(function FirePolygon({
  positions,
  eventId,
  idx
}: {
  positions: LatLngExpression[] | LatLngExpression[][];
  eventId: string;
  idx?: number;
}) {
  return (
    <Polygon
      key={idx !== undefined ? `${eventId}-${idx}` : eventId}
      positions={positions}
      pane={FIRE_BOUNDARY_PANE}
      pathOptions={{
        color: RFS_BOUNDARY_STROKE,
        fillColor: RFS_BOUNDARY_FILL,
        fillOpacity: 0.35,
        weight: 3,
        opacity: 1,
        interactive: false, // Allow clicks to pass through to hotspots/markers underneath
      }}
    />
  );
});

// Minimum zoom level to show fire boundaries
// Below this zoom, polygons are too small to be useful and hurt performance
const MIN_ZOOM_FOR_BOUNDARIES = 7;

/** Stable bounds representation using primitives to prevent infinite re-renders */
interface StableBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

const FireBoundaryLayer = memo(function FireBoundaryLayer({ events }: FireBoundaryLayerProps) {
  const map = useMap();
  const [stableBounds, setStableBounds] = useState<StableBounds | null>(null);
  const [zoom, setZoom] = useState<number>(map.getZoom());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update view bounds and zoom on map move/zoom with debouncing
  useEffect(() => {
    const updateBounds = () => {
      // Debounce to prevent rapid updates during popup auto-pan
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        const bounds = map.getBounds();
        // Store as primitives to prevent object reference changes causing re-renders
        setStableBounds({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        });
        setZoom(map.getZoom());
      }, 100);
    };

    // Initial bounds (with delay to let map settle)
    const initialTimeout = setTimeout(() => {
      const bounds = map.getBounds();
      setStableBounds({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
      setZoom(map.getZoom());
    }, 100);

    // Listen for map movement
    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);

    return () => {
      clearTimeout(initialTimeout);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  // Filter and prepare visible boundaries
  // IMPORTANT: useMemo must be called unconditionally (React hooks rules)
  const visibleBoundaries = useMemo(() => {
    // Return empty if zoom is too low (performance optimization)
    if (zoom < MIN_ZOOM_FOR_BOUNDARIES) return [];
    if (!stableBounds) return [];

    return events
      .filter(event => {
        // Look for either:
        // 1. BURN_AREA events with burnGeometry (VIC OSOM actual fire perimeters)
        // 2. FIRE_INCIDENT events with boundaryGeometry (NSW RFS fire boundaries)
        // 3. FIRE_INCIDENT events with fireBoundary (WA DFES fire boundaries)
        const hasBurnGeometry = event.type === 'BURN_AREA' && event.data?.burnGeometry;
        const hasBoundaryGeometry = event.type === 'FIRE_INCIDENT' && event.data?.boundaryGeometry;
        const hasFireBoundary = event.type === 'FIRE_INCIDENT' && event.data?.fireBoundary;

        if (!hasBurnGeometry && !hasBoundaryGeometry && !hasFireBoundary) {
          return false;
        }

        const geometry = (event.data?.burnGeometry || event.data?.boundaryGeometry || event.data?.fireBoundary) as {
          type: string;
          coordinates: number[][][] | number[][][][];
        };

        // Calculate bounding box and check intersection
        const bbox = getBoundingBox(geometry);
        if (!bbox) return false;

        // Check bounds intersection using stable primitive values
        const [minLat, minLon, maxLat, maxLon] = bbox;
        return !(maxLat < stableBounds.south || minLat > stableBounds.north ||
                 maxLon < stableBounds.west || minLon > stableBounds.east);
      })
      .map(event => ({
        event,
        geometry: (event.data?.burnGeometry || event.data?.boundaryGeometry || event.data?.fireBoundary) as {
          type: string;
          coordinates: number[][][] | number[][][][];
        },
      }));
  }, [events, stableBounds?.north, stableBounds?.south, stableBounds?.east, stableBounds?.west, zoom]);

  if (visibleBoundaries.length === 0) {
    return null;
  }

  return (
    <Pane name={FIRE_BOUNDARY_PANE} style={{ zIndex: FIRE_BOUNDARY_PANE_Z }}>
      {visibleBoundaries.map(({ event, geometry }) => {
        const positions = getPolygonPositions(geometry);
        if (!positions || positions.length === 0) return null;

        // For MultiPolygon, render each polygon separately
        if (geometry.type === 'MultiPolygon') {
          return positions.map((polygonPositions, idx) => (
            <FirePolygon
              key={`${event.id}-${idx}`}
              positions={polygonPositions}
              eventId={event.id}
              idx={idx}
            />
          ));
        }

        // For regular Polygon with holes, use the positions array directly
        return (
          <FirePolygon
            key={event.id}
            positions={positions}
            eventId={event.id}
          />
        );
      })}
    </Pane>
  );
});

export default FireBoundaryLayer;
